import { TruckDto } from '@trucktrax/trucktrax-ts-common';
import { PayloadAction } from '../../types';
import {
  EVICT_TRUCK_FROM_CACHE,
  FETCH_ALL_TRUCKS,
  FETCH_TRUCKS,
  UPDATE_TRUCK_ASSIGNMENT,
} from '../../constants/actionConstants';
import { TruckEviction } from '../actions/truckActions';

export const trucksReducer = (
  state = { trucks: [] as TruckDto[] },
  action: PayloadAction<TruckDto[], any>,
) => {
  switch (action.type) {
    case FETCH_TRUCKS:
      return {
        ...state,
        trucks: action.payload,
      };
    default:
      return state;
  }
};

type AllTrucksAction =
  | PayloadAction<TruckDto[], 'FETCH_ALL_TRUCKS'>
  | PayloadAction<TruckDto, 'UPDATE_TRUCK_ASSIGNMENT'>
  | PayloadAction<TruckEviction, 'EVICT_TRUCK_FROM_CACHE'>;

export const allTrucksReducer = (state: TruckDto[] = [], action: AllTrucksAction): TruckDto[] => {
  switch (action.type) {
    case FETCH_ALL_TRUCKS:
      return action.payload;
    case UPDATE_TRUCK_ASSIGNMENT:
      return applyTruckAssignment(state, action.payload);
    case EVICT_TRUCK_FROM_CACHE:
      return evictTruck(state, action.payload);
    default:
      return state;
  }
};

/**
 * True when `record` is strictly newer than `event` by the server's
 * `updatedTimestamp`.
 *
 * drivers-ms stamps `updatedTimestamp` on every save, and the REST truck list and
 * the socket event serialize it the same way (UTC, millisecond precision), so the
 * two compare directly. A missing or unparseable stamp on either side parses to
 * NaN, which compares false: the event is applied in arrival order, as it was
 * before this guard.
 */
function isNewer(record: { updatedTimestamp?: string }, event: { updatedTimestamp?: string }) {
  return Date.parse(record.updatedTimestamp ?? '') > Date.parse(event.updatedTimestamp ?? '');
}

/**
 * Drops a truck that is no longer in the dispatcher's region + product line.
 * Returns the same array when it was never cached, so an event for a truck
 * this dispatcher never had does not churn every downstream selector.
 *
 * A cached record newer than the eviction wins: two quick region moves
 * (A -> B -> A) can arrive inverted, and the older move-out must not drop a
 * truck that is back in view.
 */
function evictTruck(state: TruckDto[], eviction: TruckEviction): TruckDto[] {
  const next = state.filter(truck => truck.url !== eviction.url || isNewer(truck, eviction));
  return next.length === state.length ? state : next;
}

/**
 * Folds a single truck's assignment change into the cached list (DEV-3840).
 *
 * Identity is `url`, not `id` -- url is what buildTruckMap and every driver
 * lookup in the message center key off, and ids are not unique across tenants.
 *
 * Only `driver` is taken from the payload. The producer's unassign paths
 * (`trucks/logout-trucks`, region-wide `trucks/logout`) load trucks without
 * their relations, so `plant`, `vehicleType`, `haulers`, `defaultDriver` and the
 * tares arrive as present-but-null; merging the whole payload would blank those
 * fields on the cached record for every `allTrucks` consumer -- map markers, the
 * recipient picker -- until the next refetch. Driver assignment is the only
 * change this event reports, so it is the only change applied.
 *
 * A truck the list has not seen is appended when the event brings an assignment:
 * the socket group is region-scoped and the caller has already filtered on the
 * selected product line, so anything reaching here belongs in the current view.
 * An unassign for an uncached truck is dropped instead -- there is nothing to
 * correct, and seeding the cache from a relation-less payload would put a
 * half-populated truck in front of the dispatcher. (That also drops a driverless
 * truck an admin moves into this view; every `allTrucks` consumer only looks a
 * truck up by driver or by a tracked position, so it is missed by nothing until
 * the next refetch or assignment brings it in.)
 *
 * Events are ordered by `updatedTimestamp`, not arrival: the producer publishes
 * the unassign and the assign of a re-login to the same truck from two
 * fire-and-forget tasks, so they can arrive inverted. An event older than the
 * cached record for its truck is dropped whole, its cross-truck clear included --
 * that also skips a mutation replayed after a refetch whose snapshot is newer; one
 * with the same stamp re-applies the value the snapshot already holds.
 */
function applyTruckAssignment(state: TruckDto[], incoming: TruckDto): TruckDto[] {
  const driverUrl = incoming.driver?.url;
  let matched = false;
  let stale = false;

  const next = state.map(truck => {
    if (truck.url === incoming.url) {
      matched = true;
      if (isNewer(truck, incoming)) {
        stale = true;
        return truck;
      }

      // `driver` is assigned explicitly rather than spread: the payload is the
      // truck's post-change state, so an unassign is an absent driver -- and a
      // producer that omits the key (rather than sending null) must still clear
      // it, which is the one case this event exists to handle. The stamp is
      // carried so the next event for this truck is ordered against this one.
      return {
        ...truck,
        driver: incoming.driver,
        updatedTimestamp: incoming.updatedTimestamp ?? truck.updatedTimestamp,
      };
    }

    // A driver occupies exactly one truck. If the assign for the new truck
    // lands before (or without) the unassign for the old one, leaving the
    // stale pairing in place would put the driver on two trucks and let
    // buildTruckMap resolve their alias by list order. Not ordered by stamp: a
    // truck's updatedTimestamp moves on any save (a tare, a plate), and nothing
    // stops the database holding one driver on two trucks, so another truck's
    // stamp says nothing about when this driver got there. The clear is only as
    // ordered as its own event: a stale event is dropped below, this clear with it.
    if (driverUrl && truck.driver?.url === driverUrl) {
      return { ...truck, driver: undefined };
    }

    return truck;
  });

  if (stale) {
    return state;
  }

  if (matched) {
    return next;
  }

  return driverUrl ? [...next, incoming] : next;
}
